import React, { useState, useEffect } from 'react';
import { Breadcrumb, Layout, theme, Space, Tag, Form, Input, Button, Select, InputNumber, Col, Row, Switch, Spin, DatePicker, Table, Popconfirm, Tooltip } from 'antd';
import { AlertError, AlertSuccess } from '../../usableclasses.js';
import axios from 'axios';
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../../appsettings.js';
import { Link, useNavigate, useParams } from "react-router-dom";
import dayjs from 'dayjs'; 
import { DeleteOutlined } from '@ant-design/icons';

const { Content } = Layout;

const ProductUpdate = () => {
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const UPDATED_BY = localStorage.getItem("USERID");

    const [form] = Form.useForm();
    const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();
    const [deleting, setDeleting] = useState(false);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [productTypes, setProductTypes] = useState([]);
    const [segments, setSegments] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [uoms, setUoms] = useState([]);
    const [positions, setPositions] = useState([]);
    const [development, setDevelopment] = useState([])
    const [loading, setLoading] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [productDetails, setProductDetails] = useState(null);
    const [linkedProducts, setLinkedProducts] = useState([]);
    const [linkedProductOptions, setLinkedProductOptions] = useState([]);
    const [selectedLinkedProduct, setSelectedLinkedProduct] = useState(null);
    const [isComboProduct, setIsComboProduct] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        fetchDropdownData().then(() => {
            fetchProductData();
        });
    }, []);

    const fetchProductData = async () => {
        setLoading(true);
        try {
            if (!id) {
                AlertError("Invalid product ID. Redirecting back to product list.");
                navigate("/master/productlist");
                return;
            }

            const headers = { SystemAccessToken: localStorage.getItem("AUTHCODE") };
            const response = await axios.get(`${MAINAPIURL}/products/getdetailbyid/${id}`, { headers });

            if (!response.data) {
                throw new Error("Product data not found");
            }

            const details = response.data;
            setProductDetails(details);

            setIsComboProduct(details.proUseAs === 'Combo');
            setSelectedCategoryId(details.catId);

            const introDate = details.proIntroDate ? dayjs(details.proIntroDate) : null;
            const discoDate = details.proDiscoDate ? dayjs(details.proDiscoDate) : null;

            form.setFieldsValue({
                proUseAs: details.proUseAs || 'Normal',
                proSku: details.proSku || '',
                proName: details.proName || '',
                proSize: details.proSize || '',
                catId: {
                    key: details.catId,
                    value: details.catId,
                    label: details.catName || ''
                },
                proBrandId: details.brandId,
                proType: {
                    key: details.proTypeId,
                    value: details.proTypeId,
                    label: details.proTypeName || ''
                },
                proPositionId: details.positionId.toString(),
                proSegmentId: details.segmentId.toString(),
                proDevelopment: details.development,
                proSectorId: details.sectorId.toString(),
                proUomId: details.uomId.toString(),
                proSalePrice: details.proOfferPrice || 0,
                proStandardPrice: details.proStandardPrice || 0,
                proCostPrice: details.proCostPrice || 0,
                proIntroduceDate: introDate,
                proDiscontinueDate: discoDate,
                proIsSale: !!details.proIsSale,
                proIsActive: details.proIsActive !== false
            });

        } catch (error) {
            console.error(error);
            AlertError("We found some error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (productDetails && isComboProduct) {
            fetchLinkedProducts();
            fetchLinkedProductOptions();
        }
    }, [productDetails, isComboProduct]);

    const fetchLinkedProducts = async () => {
        try {
            const headers = { SystemAccessToken: TOKEN };
            const response = await axios.get(`${ApiURL}/products/getlinkproductsbyid/${id}`, { headers });

            if (response.data) {
                setLinkedProducts(response.data);
            }
        } catch (error) {
            AlertError("Failed to load linked products");
        }
    };

    const fetchLinkedProductOptions = async () => {
        try {
            const headers = { SystemAccessToken: TOKEN };
            const response = await axios.get(`${ApiURL}/products/dropdownforlinkedproducts/${id}`, { headers });

            if (response.data) {
                setLinkedProductOptions(response.data);
            }
        } catch (error) {
            AlertError("Failed to load linked product options");
        }
    };

    const handleAddLinkedProduct = async () => {
        if (!selectedLinkedProduct) {
            AlertError("Please select a product to link");
            return;
        }

        try {
            setLoading(true);
            const headers = { SystemAccessToken: TOKEN };
            await axios.get(`${ApiURL}/products/addlinkedproduct/${id}/${selectedLinkedProduct}`, { headers });

            AlertSuccess("Product linked successfully");
            fetchLinkedProducts();
            fetchLinkedProductOptions();
            setSelectedLinkedProduct(null);
        } catch (error) {
            AlertError("Failed to link product");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteLinkedProduct = async (linkProductId) => {
        try {
            setLoading(true);
            const headers = { SystemAccessToken: TOKEN };
            await axios.get(`${ApiURL}/products/deletelinkedproduct/${id}/${linkProductId}`, { headers });

            AlertSuccess("Product unlinked successfully");
            fetchLinkedProducts();
            fetchLinkedProductOptions();
        } catch (error) {
            AlertError("Failed to unlink product");
        } finally {
            setLoading(false);
        }
    };

    const linkedProductColumns = [
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            width: '80px',
            render: (_, record) => (
                <Popconfirm
                    title="Are you sure to delete this product?"
                    onConfirm={() => handleDeleteLinkedProduct(record.linkedProId)}
                    okText="Yes"
                    cancelText="No"
                    disabled={loading || deleting}
                >
                    <Tooltip title="Delete">
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={loading || deleting}
                        />
                    </Tooltip>
                </Popconfirm>

            ),
        },
        { title: 'Code', dataIndex: 'linkedProId', key: 'linkedProId' },
        { title: 'Product Name', dataIndex: 'linkedProName', key: 'linkedProName' },
        { title: 'Size', dataIndex: 'linkedProSize', key: 'linkedProSize' },
        {
            title: 'Offer Price',
            dataIndex: 'linkedProOfferMrp',
            key: 'linkedProOfferMrp',
            render: (price) => `${parseFloat(price).toFixed(2)}`
        },
        {
            title: 'Standard Price',
            dataIndex: 'linkedProStandardMrp',
            key: 'linkedProStandardMrp',
            render: (price) => `${parseFloat(price).toFixed(2)}`
        },
        {
            title: 'Cost Price',
            dataIndex: 'linkedProCostPrice',
            key: 'linkedProCostPrice',
            render: (price) => `${parseFloat(price).toFixed(2)}`
        },
        {
            title: 'On Sale',
            dataIndex: 'linkedProIsSale',
            key: 'linkedProIsSale',
            render: (isSale) => (
                <Tag color={isSale === true ? "green" : "red"}>
                    {isSale === true ? "Yes" : "No"}
                </Tag>),
        },
        {
            title: 'Status',
            dataIndex: 'linkedProIsActive',
            key: 'linkedProIsActive',
            render: (isActive) => (
                <Tag color={isActive === true ? "green" : "red"}>
                    {isActive === true ? "Active" : "Inactive"}
                </Tag>),
        }
    ];

    const fetchDropdownData = async () => {
        setLoading(true);
        try {
            const [categoriesRes, brandsRes, productTypesRes, sectorsRes, uomRes, segmentsRes, positionsRes] = await Promise.all([
                axios.get(`${ApiURL}/master/category/all`, { headers: { SystemAccessToken: TOKEN } }),
                axios.get(`${ApiURL}/master/brands/all`, { headers: { SystemAccessToken: TOKEN } }),
                axios.get(`${ApiURL}/master/productstype/dropdown`, { headers: { SystemAccessToken: TOKEN } }),
                axios.get(`${ApiURL}/master/sector/dropdown`, { headers: { SystemAccessToken: TOKEN } }),
                axios.get(`${ApiURL}/master/uom/dropdown`, { headers: { SystemAccessToken: TOKEN } }),
                axios.get(`${ApiURL}/master/segment/dropdown`, { headers: { SystemAccessToken: TOKEN } }),
                axios.get(`${ApiURL}/master/position/dropdown`, { headers: { SystemAccessToken: TOKEN } }),
            ]);

            const categoryOptions = categoriesRes.data
                .filter(category => category?.categoryId && category?.categoryName)
                .map(category => ({ id: category.categoryId, label: category.categoryName }));

            const brandOptions = brandsRes.data
                .filter(brand => brand?.brandId && brand?.brandName)
                .map(brand => ({ id: brand.brandId, label: brand.brandName }));

            setSectors(sectorsRes.data);
            setDevelopment([{ label: "LOCAL", id: "LOCAL" }, { label: "INT", id: "INT" }]);
            setSegments(segmentsRes.data);
            setUoms(uomRes.data);
            setPositions(positionsRes.data);
            setCategories(categoryOptions);
            setBrands(brandOptions);
            setProductTypes(Array.isArray(productTypesRes.data) ? productTypesRes.data : []);

            return { categories: categoryOptions, brands: brandOptions };
        } catch (error) {
            AlertError('Failed to load dropdown data');
            return { categories: [], brands: [], productTypes: [] };
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchSubcategories = async () => {
            if (!selectedCategoryId) return;
            setLoading(true);
            try {
                const response = await axios.get(`${ApiURL}/master/subcategory/listbycategoryid/${selectedCategoryId}`, {
                    headers: { SystemAccessToken: TOKEN }
                });
                const subcategoryOptions = Array.isArray(response.data) ?
                    response.data
                        .filter(sub => sub?.subCategoryId && sub?.subCategoryName?.trim())
                        .map(sub => ({ id: sub.subCategoryId, label: sub.subCategoryName })) : [];
                setSubcategories(subcategoryOptions);
            } catch (error) {
                setSubcategories([]);
            } finally {
                setLoading(false);
            }
        };
        fetchSubcategories();
    }, [selectedCategoryId]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const payload = {
                proId: parseInt(id),
                proSku: values.proSku,
                proName: values.proName,
                brandId: parseInt(values.proBrandId?.value || values.proBrandId),
                catId: parseInt(values.catId?.value || values.catId),
                sectorId: parseInt(values.proSectorId?.value || values.proSectorId),
                proTypeId: parseInt(values.proType?.value || values.proType),
                segmentId: parseInt(values.proSegmentId?.value || values.proSegmentId),
                development: values.proDevelopment?.value || values.proDevelopment,
                positionId: parseInt(values.proPositionId?.value || values.proPositionId),
                proSize: values.proSize,
                uomId: parseInt(values.proUomId?.value || values.proUomId),
                proCostPrice: parseFloat(values.proCostPrice || 0),
                proOfferPrice: parseFloat(values.proSalePrice),
                proStandardPrice: parseFloat(values.proStandardPrice),
                proIntroDate: values.proIntroduceDate ? values.proIntroduceDate.format('YYYY-MM-DD') : null,
                proDiscoDate: values.proDiscontinueDate ? values.proDiscontinueDate.format('YYYY-MM-DD') : null,
                proIsSale: values.proIsSale || false,
                proIsActive: values.proIsActive ?? true,
                proLatestCreatedBy: UPDATED_BY
            };

            const headers = { SystemAccessToken: TOKEN, "Content-Type": "application/json" };
            await axios.post(`${ApiURL}/products/edit`, payload, { headers });
            AlertSuccess("Product updated successfully.");
            navigate('/master/productlist');

        } catch (error) {
            AlertError(error.response?.data?.message || error.message || "Failed to update product!");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Spin spinning={loading} tip="Processing...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: "16px 0" }}>
                        <Breadcrumb.Item>Master</Breadcrumb.Item>
                        <Breadcrumb.Item><Link to={'/master/productlist'}>Products</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>Edit Product</Breadcrumb.Item>
                    </Breadcrumb>
                    <div style={{ padding: 10, minHeight: '65vh', background: colorBgContainer, borderRadius: borderRadiusLG }}>
                        <Space direction="vertical" style={{ width: "100%", padding: "15px" }}>
                            <div style={{ background: colorBgContainer, padding: 24, borderRadius: borderRadiusLG }}>
                                <h2>Edit Product</h2>
                                <Form form={form} layout="vertical" onFinish={onFinish}>
                                    <Row gutter={16}>
                                        <Col sm={24} lg={4}><Form.Item label="Use As" name="proUseAs" rules={[{ required: true, message: 'Use As is required' }]}><Select disabled options={[{ value: 'Normal' }, { value: 'Combo' }]} /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="Code" name="proSku" rules={[{ required: true, message: 'Code is required' }]}><Input /></Form.Item></Col>
                                        <Col sm={24} lg={8}><Form.Item label="Product Name" name="proName" rules={[{ required: true, message: 'Product Name is required' }]}><Input /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="Size" name="proSize" rules={[{ required: true, message: 'Size is required' }]}><Input /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="UOM" name="proUomId" rules={[{ required: true, message: 'UOM is required' }]}><Select options={uoms.map(x => ({ value: x.id, label: x.label }))} /></Form.Item></Col>
                                    </Row>

                                    <Row gutter={16}>
                                        <Col sm={24} lg={4}><Form.Item label="Brand" name="proBrandId" rules={[{ required: true, message: 'Brand is required' }]}><Select options={brands.map(x => ({ value: x.id, label: x.label }))} /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="Category" name="catId" rules={[{ required: true, message: 'Category is required' }]}><Select options={categories.map(x => ({ value: x.id, label: x.label }))} /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="Sector" name="proSectorId" rules={[{ required: true, message: 'Sector is required' }]}><Select options={sectors.map(x => ({ value: x.id, label: x.label }))} /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="Type" name="proType" rules={[{ required: true, message: 'Product Type is required' }]}><Select options={productTypes.map(x => ({ value: x.id, label: x.label }))} /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="Segment" name="proSegmentId" rules={[{ required: true, message: 'Segment is required' }]}><Select options={segments.map(x => ({ value: x.id, label: x.label }))} /></Form.Item></Col>
                                    </Row>

                                    <Row gutter={16}>
                                        <Col sm={24} lg={4}><Form.Item label="Development" name="proDevelopment" rules={[{ required: true, message: 'Please select development' }]}><Select options={development.map(x => ({ value: x.label, label: x.label }))} /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="Position" name="proPositionId" rules={[{ required: true, message: 'Please select position' }]}><Select options={positions.map(x => ({ value: x.id, label: x.label }))} /></Form.Item></Col>
                                        <Col sm={24} lg={4}>
                                            <Form.Item label="Introduce Date" name="proIntroduceDate" rules={[{ required: true, message: 'Please select introduce date' }]}>
                                                {/* CHANGE 3: Added Format prop explicitly */}
                                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                                            </Form.Item>
                                        </Col>
                                        <Col sm={24} lg={4}>
                                            <Form.Item label="Discontinue Date" name="proDiscontinueDate" rules={[{ required: true, message: 'Please select discontinue date' }]}>
                                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                                            </Form.Item>
                                        </Col>
                                        <Col sm={24} lg={4}><Form.Item label="For Sale" name="proIsSale" valuePropName="checked"><Switch /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="Active Status" name="proIsActive" valuePropName="checked" initialValue={true}><Switch defaultChecked /></Form.Item></Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col sm={24} lg={4}><Form.Item label="Offer Price" name="proSalePrice" rules={[{ required: true, message: 'Please enter sales price' }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="Standard Price" name="proStandardPrice" rules={[{ required: true, message: 'Standard Price is required' }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                                        <Col sm={24} lg={4}><Form.Item label="Cost Price" name="proCostPrice" rules={[{ required: true, message: 'Cost Price is required' }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                                        <Col sm={24} lg={4} style={{ textAlign: 'right', paddingTop: 30 }}>
                                            <Button type="primary" htmlType="submit" loading={loading} style={{ minWidth: '150px' }}>
                                                {loading ? "Updating..." : "Update Product"}
                                            </Button>
                                        </Col>
                                    </Row>

                                    {isComboProduct && (
                                        <>
                                            <h3 style={{ marginTop: '20px' }}>Linked Products</h3>
                                            <Row gutter={16}>
                                                <Col xs={24} md={8} style={{ padding: '5px' }}>
                                                    <Form.Item label="Product Linked">
                                                        <Select
                                                            placeholder="Select product to link"
                                                            value={selectedLinkedProduct}
                                                            onChange={(value) => setSelectedLinkedProduct(value)}
                                                            showSearch
                                                            filterOption={(input, option) =>
                                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                            }
                                                        >
                                                            {linkedProductOptions.map((product) => (
                                                                <Select.Option key={product.id} value={product.id}>
                                                                    {product.label}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={4} style={{ padding: '5px', marginTop: '29px' }}>
                                                    <Button type="primary" onClick={handleAddLinkedProduct}>
                                                        Add Linked Product
                                                    </Button>
                                                </Col>
                                            </Row>

                                            <Table
                                                columns={linkedProductColumns}
                                                dataSource={linkedProducts}
                                                rowKey="proLinkedId"
                                                pagination={false}
                                                style={{ marginBottom: '20px' }}
                                            />
                                        </>
                                    )}
                                </Form>
                            </div>
                        </Space>
                    </div>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default ProductUpdate;